import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { api, TICKET_TYPEN, type Ticket, type TicketVerlaufEintrag } from '../lib/api';
import { useTicketNeu } from '../components/TicketNeu';
import { fmtDatum, fmtDatumZeit, istAbgeschlossen, statusInfo, stepper, type Schritt } from '../lib/ticketStatus';

// „Meine Tickets“ — für alle, die Tickets melden: Status auf einen Blick (Gemeldet → In Arbeit → Erledigt),
// wer sich kümmert, was zuletzt passiert ist, auf Rückfragen antworten, „doch nicht erledigt“.

const PUNKT: Record<Schritt['zustand'], string> = {
  fertig: 'bg-emerald-500 border-emerald-500 text-white',
  aktiv: 'bg-sky-500 border-sky-500 text-white ring-4 ring-sky-100',
  offen: 'bg-white border-slate-300 text-slate-300',
  warnung: 'bg-amber-400 border-amber-400 text-white ring-4 ring-amber-100',
  abgelehnt: 'bg-slate-500 border-slate-500 text-white',
};
const PUNKT_ZEICHEN: Record<Schritt['zustand'], string> = { fertig: '✓', aktiv: '●', offen: '', warnung: '?', abgelehnt: '✕' };

export function Stepper({ status }: { status: string }) {
  const schritte = stepper(status);
  return (
    <ol data-testid="ticket-stepper" className="flex items-center w-full" aria-label="Fortschritt">
      {schritte.map((s, i) => (
        <li key={s.key} data-zustand={s.zustand} className={`flex items-center ${i < schritte.length - 1 ? 'flex-1' : ''}`}>
          <div className="flex flex-col items-center gap-1 min-w-[64px]">
            <span className={`w-6 h-6 rounded-full border-2 text-[11px] font-bold flex items-center justify-center ${PUNKT[s.zustand]}`}>{PUNKT_ZEICHEN[s.zustand]}</span>
            <span className={`text-[11px] leading-none ${s.zustand === 'offen' ? 'text-slate-400' : 'text-slate-700 font-medium'}`}>{s.label}</span>
          </div>
          {i < schritte.length - 1 && (
            <span className={`flex-1 h-0.5 mx-1 -mt-4 ${schritte[i + 1]!.zustand === 'offen' ? 'bg-slate-200' : 'bg-emerald-400'}`} />
          )}
        </li>
      ))}
    </ol>
  );
}

function ereignisText(e: TicketVerlaufEintrag): { icon: string; text: string; detail?: string | null } {
  if (e.art === 'angelegt') return { icon: '📝', text: 'Ticket gemeldet' };
  if (e.art === 'zuweisung') return { icon: '👤', text: e.ziel_name ? `${e.ziel_name} kümmert sich darum` : 'Zuständigkeit aufgehoben' };
  if (e.art === 'kommentar') return { icon: '💬', text: `${e.von ?? '?'} schreibt`, detail: e.text };
  const s = statusInfo(e.nach_wert ?? '');
  const mit = e.von ? ` (${e.von})` : '';
  if (e.nach_wert === 'abgelehnt') return { icon: '🚫', text: `Abgelehnt${mit}`, detail: e.text ? `Grund: ${e.text}` : null };
  if (e.nach_wert === 'rueckfrage') return { icon: '❓', text: `Rückfrage${mit}`, detail: e.text };
  if (e.nach_wert === 'erledigt') return { icon: '✅', text: `Erledigt${mit}`, detail: e.text };
  if (e.nach_wert === 'offen' && e.von_wert === 'erledigt') return { icon: '↩️', text: `Wieder geöffnet${mit}`, detail: e.text };
  return { icon: '🔄', text: `Status: ${s.label}${mit}`, detail: e.text };
}

export function Zeitleiste({ eintraege }: { eintraege: TicketVerlaufEintrag[] }) {
  return (
    <ol data-testid="ticket-zeitleiste" className="space-y-2 border-l-2 border-slate-100 pl-4 ml-1">
      {eintraege.map((e, i) => {
        const x = ereignisText(e);
        return (
          <li key={i} className="relative text-sm">
            <span className="absolute -left-[25px] top-0 bg-white text-base leading-none" aria-hidden>{x.icon}</span>
            <div className="text-slate-800">{x.text} <span className="text-[11px] text-slate-400">· {fmtDatumZeit(e.am)}</span></div>
            {x.detail && <div className="text-slate-600 bg-slate-50 rounded-lg px-3 py-1.5 mt-0.5 whitespace-pre-line">{x.detail}</div>}
          </li>
        );
      })}
    </ol>
  );
}

function MeinTicket({ t, offenStart, onChange }: { t: Ticket; offenStart: boolean; onChange: () => void }) {
  const { hatHinweis, melderGesehen } = useTicketNeu();
  const [auf, setAuf] = useState(offenStart);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const verlauf = useApi(() => api.tickets.verlauf(t.id), [t.id, t.updated_at, t.status], auf);
  const neu = hatHinweis(t.id);
  const ref = useRef<HTMLDivElement>(null);
  const s = statusInfo(t.status);
  const ty = TICKET_TYPEN[t.typ] ?? TICKET_TYPEN.fehler;
  const frage = t.status === 'rueckfrage';
  const liste = verlauf.data?.data ?? [];
  const letzteAktivitaet = t.updated_at || t.created_at;

  // geöffnet = angesehen → Hinweis (Toast + Zähler) weg
  useEffect(() => { if (auf && neu) melderGesehen([t.id]); }, [auf, neu, t.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (offenStart) ref.current?.scrollIntoView?.({ block: 'center' }); }, [offenStart]);

  const senden = async () => {
    if (!text.trim()) return;
    setBusy(true);
    try { await api.tickets.kommentar(t.id, text.trim()); setText(''); verlauf.reload(); onChange(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };
  const [wiederAuf, setWiederAuf] = useState(false);
  const [wiederText, setWiederText] = useState('');
  const wiederOeffnen = async () => {
    setBusy(true);
    try { await api.tickets.wiederOeffnen(t.id, wiederText.trim() || undefined); setWiederAuf(false); setWiederText(''); verlauf.reload(); onChange(); }
    catch (e) { alert('Fehler: ' + (e as Error).message); }
    setBusy(false);
  };

  return (
    <div ref={ref} data-testid="mein-ticket" data-status={t.status}
         className={`bg-white border rounded-xl p-4 ${frage ? 'border-amber-400 ring-2 ring-amber-100' : neu ? 'border-sky-400 ring-2 ring-sky-100' : 'border-slate-200'} ${istAbgeschlossen(t.status) && !neu ? 'opacity-80' : ''}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {neu && <span data-testid="mein-ticket-neu" className="inline-block bg-sky-500 text-white font-bold rounded text-[10px] px-1.5 py-0.5 tracking-wide">NEU</span>}
            <span className="text-[11px] font-mono font-semibold px-1.5 py-0.5 rounded bg-slate-800 text-white">#T-{t.id}</span>
            <span className="text-[11px] text-slate-500">{ty.icon} {ty.label}</span>
            <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>
          </div>
          <div className="font-semibold text-slate-900 mt-1">{t.titel}</div>
        </div>
        <div className="w-full sm:w-72 shrink-0"><Stepper status={t.status} /></div>
      </div>

      {frage && (
        <div data-testid="rueckfrage-banner" className="mt-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-lg px-3 py-2 text-sm font-medium">
          ❓ Wartet auf deine Antwort — bitte unten kurz antworten, dann geht es weiter.
        </div>
      )}

      <div className="text-xs text-slate-500 mt-3 flex items-center gap-x-4 gap-y-1 flex-wrap">
        <span>{t.zugewiesen_an ? <>👤 Bearbeiter: <b className="text-slate-700">{t.zugewiesen_name || t.zugewiesen_kurzel}</b></> : '👤 Noch nicht übernommen'}</span>
        <span>gemeldet {fmtDatum(t.created_at)}</span>
        <span>zuletzt geändert {fmtDatumZeit(letzteAktivitaet)}</span>
        {t.erledigt_at && <span>{t.status === 'abgelehnt' ? 'abgelehnt' : 'erledigt'} {fmtDatum(t.erledigt_at)}</span>}
        <button data-testid="mein-ticket-auf" onClick={() => setAuf(a => !a)} className="text-slate-600 hover:text-slate-900 font-medium">
          {auf ? '▾ schließen' : `▸ Verlauf${t.komm_anzahl ? ` · ${t.komm_anzahl} Nachricht${t.komm_anzahl === 1 ? '' : 'en'}` : ''}`}
        </button>
      </div>

      {auf && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-3">
          {verlauf.loading && liste.length === 0 && <div className="text-xs text-slate-400">Lädt …</div>}
          {liste.length > 0 && <Zeitleiste eintraege={liste} />}
          {t.status !== 'abgelehnt' && (
            <div className="flex gap-1.5">
              <input data-testid="mein-ticket-text" className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
                     placeholder={frage ? 'Deine Antwort …' : 'Nachricht schreiben …'} value={text}
                     onChange={e => setText(e.target.value)}
                     onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void senden(); } }} />
              <button data-testid="mein-ticket-senden" disabled={busy || !text.trim()} onClick={() => void senden()}
                      className={`text-sm px-3 py-1.5 rounded-lg text-white hover:opacity-90 disabled:opacity-40 ${frage ? 'bg-amber-600' : 'bg-slate-800'}`}>
                {frage ? 'Antworten' : 'Senden'}
              </button>
            </div>
          )}
          {t.status === 'erledigt' && !wiederAuf && (
            <button data-testid="mein-ticket-wieder" onClick={() => setWiederAuf(true)}
                    className="text-sm px-3 py-1.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50">
              ↩ Doch nicht erledigt
            </button>
          )}
          {t.status === 'erledigt' && wiederAuf && (
            <div className="flex gap-1.5">
              <input data-testid="mein-ticket-wieder-text" className="flex-1 border border-slate-300 rounded-lg px-3 py-1.5 text-sm"
                     placeholder="Was klappt noch nicht? (kurz)" value={wiederText} onChange={e => setWiederText(e.target.value)} />
              <button data-testid="mein-ticket-wieder-ok" disabled={busy} onClick={() => void wiederOeffnen()}
                      className="text-sm px-3 py-1.5 rounded-lg bg-slate-800 text-white hover:opacity-90 disabled:opacity-40">Wieder öffnen</button>
              <button onClick={() => setWiederAuf(false)} className="text-sm px-2 text-slate-500 hover:text-slate-800">Abbrechen</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function MeineTickets() {
  const [zeige, setZeige] = useState<'aktiv' | 'alle'>('aktiv');
  const [sp] = useSearchParams();
  const gewuenscht = Number(sp.get('ticket')) || 0;
  const list = useApi(() => api.tickets.list('alle', '', true), []);
  const { daten } = useTicketNeu();
  // Neuer Hinweis, während die Seite offen ist → Liste sofort nachladen
  const hinweisKey = (daten.melder ?? []).map(h => `${h.id}:${h.am}`).join(',');
  useEffect(() => { if (hinweisKey) list.reload(); }, [hinweisKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const alle = list.data?.data ?? [];
  // „Aktiv“ = nicht abgeschlossen ODER es gibt etwas Neues dazu (sonst wäre ein frisch erledigtes Ticket unsichtbar)
  //  Einmal mit Hinweis gesehene IDs bleiben „fixiert“, damit die Karte nicht unter den Augen verschwindet.
  const [fixiert, setFixiert] = useState<Set<number>>(new Set());
  useEffect(() => {
    const ids = (daten.melder ?? []).map(h => h.id).filter(id => !fixiert.has(id));
    if (ids.length) setFixiert(f => new Set([...f, ...ids]));
  }, [daten.melder]); // eslint-disable-line react-hooks/exhaustive-deps
  const aktiv = useMemo(() => alle.filter(t => !istAbgeschlossen(t.status) || fixiert.has(t.id) || t.id === gewuenscht), [alle, fixiert, gewuenscht]);
  const rows = zeige === 'aktiv' ? aktiv : alle;
  const wartet = alle.filter(t => t.status === 'rueckfrage').length;

  return (
    <div className="p-4 md:p-8 max-w-4xl">
      <header className="mb-6 flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">Ticket-Master</div>
          <h1 className="text-2xl font-bold text-slate-900">📋 Meine Tickets</h1>
          <div className="text-sm text-slate-500 mt-1">Was ich gemeldet habe — und wie weit es ist</div>
        </div>
        <div className="flex gap-1 border border-slate-200 rounded-lg p-0.5">
          {(['aktiv', 'alle'] as const).map(v => (
            <button key={v} data-testid={`mt-filter-${v}`} onClick={() => setZeige(v)}
                    className={`px-3 py-1.5 rounded text-sm font-medium ${zeige === v ? 'bg-slate-800 text-white' : 'text-slate-600 hover:bg-slate-100'}`}>
              {v === 'aktiv' ? `Aktiv (${aktiv.length})` : `Alle (${alle.length})`}
            </button>
          ))}
        </div>
      </header>

      {wartet > 0 && (
        <div data-testid="mt-wartet" className="mb-4 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3 text-sm font-medium">
          ❓ {wartet === 1 ? 'Ein Ticket wartet' : `${wartet} Tickets warten`} auf deine Antwort.
        </div>
      )}

      {list.loading && alle.length === 0 && <div className="text-slate-500">Lädt …</div>}
      {!list.loading && rows.length === 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-12 text-center text-slate-400">
          {zeige === 'aktiv' ? 'Keine offenen Tickets von dir. 🎉' : 'Du hast noch kein Ticket gemeldet.'}
          <div className="text-xs mt-2">Etwas gefunden? Links auf „🎫 Ticket melden“ klicken.</div>
        </div>
      )}
      <div className="space-y-3">
        {rows.map(t => (
          <MeinTicket key={t.id} t={t} onChange={() => list.reload()}
                      offenStart={t.status === 'rueckfrage' || t.id === gewuenscht} />
        ))}
      </div>
    </div>
  );
}
